/**
 * Un solo lugar para traducir errores de Supabase/PostgREST/Storage a mensajes en español.
 * Las funciones SQL fallan con errcode P0001 y message = CÓDIGO; el detail trae JSON.
 */
export type ErrorApp = {
  codigo: string;
  mensaje: string;
  detalle?: Record<string, unknown>;
  original?: unknown;
};

const MENSAJES: Record<string, string> = {
  NO_AUTENTICADO: "Tu sesión terminó. Vuelve a entrar.",
  SIN_EMPRESA: "Tu usuario todavía no está en ninguna empresa.",
  YA_TIENE_EMPRESA: "Este usuario ya pertenece a una empresa.",
  SOLO_ADMIN: "Solo un administrador o el dueño puede hacer esto.",
  STOCK_INSUFICIENTE: "No hay suficiente físico para esta salida.",
  CANTIDAD_INVALIDA: "La cantidad tiene que ser mayor que cero.",
  MOTIVO_INVALIDO: "Ese motivo no corresponde a este tipo de movimiento.",
  VENTA_NO_PERMITIDA: "Tus ventas las registra tu sistema de punto de venta; aquí no se capturan.",
  PRODUCTO_INACTIVO: "Ese producto no existe o está inactivo.",
  ALMACEN_INVALIDO: "Ese almacén no existe o está inactivo.",
  ZONA_INVALIDA: "Esa zona no pertenece al almacén elegido.",
  DESTINO_INVALIDO: "Elige un almacén destino distinto al de origen.",
  FOTO_INVALIDA: "La foto no se pudo asociar. Intenta registrar sin foto.",
  SKU_REQUERIDO: "Escribe la clave (SKU) del producto.",
  SKU_DUPLICADO: "Ya existe un producto con esa clave.",
  NOMBRE_REQUERIDO: "Escribe el nombre del producto.",
  NOMBRE_EMPRESA_REQUERIDO: "Escribe el nombre de tu empresa.",
  NOMBRE_USUARIO_REQUERIDO: "Escribe tu nombre.",
  MODO_VENTAS_INVALIDO: "Elige cómo registras tus ventas.",
  DATOS_REQUERIDOS: "Faltan datos obligatorios.",
  ALMACEN_DUPLICADO: "Ya hay un almacén con ese código.",
  SIN_FILAS: "No hay filas para procesar.",
  DEMASIADAS_FILAS: "El archivo trae demasiadas filas para una sola carga.",
  NO_ES_DEMO: "Esta acción solo existe en la empresa de demostración.",
  PERIODO_INVALIDO: "El periodo de rotación debe ser 30 o 90 días.",
  // Postgres / PostgREST
  "23505": "Ya existe un registro con esos datos.",
  "23503": "El registro apunta a algo que ya no existe.",
  "23514": "Los datos no cumplen una regla de la base (cantidad negativa o valor fuera de rango).",
  "23502": "Falta un dato obligatorio.",
  "22P02": "Un valor no tiene el formato esperado.",
  "22003": "Un número es demasiado grande (máximo 999,999,999.999).",
  USUARIO_INACTIVO: "Tu acceso fue desactivado. Habla con el administrador de tu empresa.",
  "42501": "No tienes permiso para hacer eso.",
  "57014": "La operación tardó demasiado. Intenta con menos filas.",
  "40P01": "Otra persona estaba moviendo lo mismo. Vuelve a intentar.",
  "40001": "Otra persona estaba moviendo lo mismo. Vuelve a intentar.",
  PGRST301: "Tu sesión expiró. Vuelve a entrar.",
  PGRST116: "No se encontró el registro.",
  // Auth
  invalid_credentials: "Correo o contraseña incorrectos.",
  "Invalid login credentials": "Correo o contraseña incorrectos.",
  email_not_confirmed: "Tu correo no está confirmado. Pide al administrador que lo active.",
  // Storage
  "413": "La foto es demasiado grande (máximo 5 MB).",
  "403": "No tienes permiso para subir aquí.",
  // Red
  FETCH_FAILED: "Sin conexión. Revisa tu internet e intenta de nuevo.",
};

export function mapearErrorSupabase(error: unknown): ErrorApp {
  if (!error) return { codigo: "DESCONOCIDO", mensaje: "Ocurrió un error." };
  const e = error as { code?: string; message?: string; details?: string | null; error?: string; status?: number; name?: string };
  const message = e.message ?? "";
  if (message.includes("Failed to fetch") || message.includes("NetworkError") || e.name === "TypeError") {
    return { codigo: "FETCH_FAILED", mensaje: MENSAJES.FETCH_FAILED, original: error };
  }
  // Funciones de negocio: code P0001 y message = CÓDIGO
  if (e.code === "P0001") {
    const codigo = message.trim();
    let detalle: Record<string, unknown> | undefined;
    if (e.details) {
      try { detalle = JSON.parse(e.details) as Record<string, unknown>; } catch { detalle = { texto: e.details }; }
    }
    return { codigo, mensaje: MENSAJES[codigo] ?? `Error: ${codigo}`, detalle, original: error };
  }
  const codigo = e.code ?? e.error ?? (e.status ? String(e.status) : "") ?? "";
  const porCodigo = codigo && MENSAJES[codigo];
  if (porCodigo) return { codigo, mensaje: porCodigo, original: error };
  const porMensaje = MENSAJES[message];
  if (porMensaje) return { codigo: message, mensaje: porMensaje, original: error };
  return { codigo: codigo || "DESCONOCIDO", mensaje: message || "Ocurrió un error.", original: error };
}

/** Registro estructurado para depurar después (Supabase Logs cubre el backend). */
export function registrarError(accion: string, error: ErrorApp, contexto: Record<string, unknown> = {}) {
  console.error(JSON.stringify({ accion, codigo: error.codigo, mensaje: error.mensaje, detalle: error.detalle, ...contexto }));
}
